import React, { useContext, useEffect, useState } from "react";
import "./MemberDashboard.css";
import { useHistory } from "react-router-dom";
import LibraryBooksIcon from "@material-ui/icons/LibraryBooks";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import BookIcon from "@material-ui/icons/Book";
import HistoryIcon from "@material-ui/icons/History";
import LocalLibraryIcon from "@material-ui/icons/LocalLibrary";
import PowerSettingsNewIcon from "@material-ui/icons/PowerSettingsNew";
import CloseIcon from "@material-ui/icons/Close";
import MenuIcon from "@material-ui/icons/Menu";
import EditIcon from "@material-ui/icons/Edit";
import CancelIcon from "@material-ui/icons/Cancel";
import { CircularProgress } from "@material-ui/core";
import { AuthContext } from "../../../Context/AuthContext";
import ProfileUpdateForm from "../../../Components/ProfileUpdateForm";
import { Button, Card, Badge } from "../../../ui";
import axios from "axios";
import moment from "moment";
import noBookCover from "../../../assets/no-book-cover.png";
import config from "../../../config";

function MemberDashboard() {
  const [active, setActive] = useState("profile");
  const [sidebar, setSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [openProfileUpdate, setOpenProfileUpdate] = useState(false);
  const history = useHistory();

  const API_URL = config.API_URL;
  const { user, dispatch } = useContext(AuthContext);
  const [memberDetails, setMemberDetails] = useState(null);
  const [reservations, setReservations] = useState([]);

  useEffect(() => {
    const getStudentProfile = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          API_URL + "api/users/student-profile/" + user._id
        );
        setMemberDetails(response.data);
        setReservations(response.data.reservations || []);
        setLoading(false);
      } catch (err) {
        console.log("Error in fetching the student profile:", err);
        setLoading(false);
      }
    };

    if (user) {
      getStudentProfile();
    } else {
      history.push('/signin');
    }
  }, [API_URL, user, history]);

  useEffect(() => {
    // Wait for data to load before scrolling
    if (!memberDetails) return;

    const hash = window.location.hash;
    const hashMap = {
      "#profile@member": "profile",
      "#activebooks@member": "active",
      "#reservedbooks@member": "reserved",
      "#history@member": "history"
    };

    if (hash && hashMap[hash]) {
      const section = hashMap[hash];
      setActive(section);

      // Scroll to section after content is rendered
      setTimeout(() => {
        const element = document.getElementById(hash.substring(1));
        if (element) {
          const headerOffset = 100;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

          window.scrollTo({
            top: Math.max(0, offsetPosition),
            behavior: 'smooth'
          });
        }
      }, 500);
    }
  }, [memberDetails]);

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      const hashMap = {
        "#profile@member": "profile",
        "#activebooks@member": "active",
        "#reservedbooks@member": "reserved",
        "#history@member": "history"
      };

      if (hash && hashMap[hash]) {
        const section = hashMap[hash];
        setActive(section);

        // Scroll to section when hash changes
        setTimeout(() => {
          const element = document.getElementById(hash.substring(1));
          if (element) {
            const headerOffset = 100;
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'smooth'
            });
          }
        }, 200);
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const logout = () => {
    localStorage.removeItem("user");
    dispatch({ type: "LOGOUT" });
    history.push('/');
  };

  const cancelReservation = async (reservationId) => {
    try {
      await axios.put(
        API_URL + "api/reservations/cancel/" + reservationId,
        { userId: user._id }
      );

      const updatedReservations = reservations.map(reservation => {
        if (reservation._id === reservationId) {
          return { ...reservation, status: 'cancelled' };
        }
        return reservation;
      });

      setReservations(updatedReservations);
    } catch (err) {
      console.log("Error cancelling reservation:", err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
      case 'cancelled':
        return 'danger';
      case 'completed':
        return 'primary';
      default:
        return 'primary';
    }
  };

  const handleOpenProfileUpdate = () => {
    setOpenProfileUpdate(true);
  };

  const handleCloseProfileUpdate = () => {
    setOpenProfileUpdate(false);
  };

  const handleProfileUpdateSuccess = (updatedData) => {
    setMemberDetails(prev => ({
      ...prev,
      ...updatedData
    }));
  };

  const setActiveAndScroll = (section) => {
    const sectionMap = {
      "profile": "profile@member",
      "active": "activebooks@member",
      "reserved": "reservedbooks@member",
      "history": "history@member"
    };

    const sectionId = sectionMap[section];
    if (!sectionId) return;

    setActive(section);
    setSidebar(false);

    // Update URL hash
    window.location.hash = sectionId;

    // Scroll to section after a brief delay to ensure DOM is ready
    setTimeout(() => {
      const element = document.getElementById(sectionId);
      if (element) {
        // Calculate scroll position accounting for fixed header
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: Math.max(0, offsetPosition),
          behavior: 'smooth'
        });
      }
    }, 200);
  };

  if (loading) {
    return (
      <div className="member-dashboard-loading">
        <CircularProgress />
      </div>
    );
  }

  const activeBooks = memberDetails?.activeTransactions?.filter(
    data => data.transactionType === "Issued"
  ) || [];

  return (
    <div className="member-dashboard">
      <div className="member-dashboard__container">
        <button
          className="member-dashboard__sidebar-toggle"
          onClick={() => setSidebar(!sidebar)}
          aria-label="Toggle sidebar"
        >
          {sidebar ? <CloseIcon /> : <MenuIcon />}
        </button>

        <aside className={`member-dashboard__sidebar ${sidebar ? 'is-open' : ''}`}>
          <div className="member-dashboard__sidebar-header">
            <LibraryBooksIcon className="member-dashboard__logo-icon" />
            <span className="member-dashboard__logo-text">Library Prime</span>
          </div>

          <nav className="member-dashboard__nav">
            <button
              className={`member-dashboard__nav-item ${active === "profile" ? "is-active" : ""}`}
              onClick={() => setActiveAndScroll("profile")}
            >
              <AccountCircleIcon className="member-dashboard__nav-icon" />
              <span>Profile</span>
            </button>
            <button
              className={`member-dashboard__nav-item ${active === "active" ? "is-active" : ""}`}
              onClick={() => setActiveAndScroll("active")}
            >
              <LocalLibraryIcon className="member-dashboard__nav-icon" />
              <span>Active Books</span>
            </button>
            <button
              className={`member-dashboard__nav-item ${active === "reserved" ? "is-active" : ""}`}
              onClick={() => setActiveAndScroll("reserved")}
            >
              <BookIcon className="member-dashboard__nav-icon" />
              <span>Reserved Books</span>
            </button>
            <button
              className={`member-dashboard__nav-item ${active === "history" ? "is-active" : ""}`}
              onClick={() => setActiveAndScroll("history")}
            >
              <HistoryIcon className="member-dashboard__nav-icon" />
              <span>History</span>
            </button>
          </nav>

          <button
            className="member-dashboard__logout"
            onClick={logout}
          >
            <PowerSettingsNewIcon className="member-dashboard__nav-icon" />
            <span>Log Out</span>
          </button>
        </aside>

        <main className="member-dashboard__main">
          <div className="member-dashboard__content">
            {/* Profile Section */}
            <section className="member-dashboard__section" id="profile@member">
              <div className="member-dashboard__section-header">
                <h2 className="member-dashboard__section-title">Profile Information</h2>
                <Button
                  variant="primary"
                  size="sm"
                  leadingIcon={<EditIcon />}
                  onClick={handleOpenProfileUpdate}
                >
                  Edit Profile
                </Button>
              </div>

              <Card padding="lg" className="member-dashboard__profile-card">
                <div className="member-dashboard__profile-header">
                  <div className="member-dashboard__profile-avatar">
                    <AccountCircleIcon style={{ fontSize: 80 }} />
                  </div>
                  <div className="member-dashboard__profile-info">
                    <h3 className="member-dashboard__profile-name">
                      {memberDetails?.userFullName}
                    </h3>
                    <Badge variant="primary" className="member-dashboard__profile-badge">
                      {memberDetails?.admissionId || memberDetails?.employeeId}
                    </Badge>
                    <p className="member-dashboard__profile-email">
                      {memberDetails?.email}
                    </p>
                    <p className="member-dashboard__profile-phone">
                      {memberDetails?.mobileNumber}
                    </p>
                  </div>
                </div>

                <div className="member-dashboard__profile-details">
                  <div className="member-dashboard__profile-grid">
                    <Card padding="md" className="member-dashboard__detail-card">
                      <span className="member-dashboard__detail-label">Age</span>
                      <span className="member-dashboard__detail-value">
                        {memberDetails?.age || "N/A"}
                      </span>
                    </Card>
                    <Card padding="md" className="member-dashboard__detail-card">
                      <span className="member-dashboard__detail-label">Gender</span>
                      <span className="member-dashboard__detail-value">
                        {memberDetails?.gender || "N/A"}
                      </span>
                    </Card>
                    <Card padding="md" className="member-dashboard__detail-card">
                      <span className="member-dashboard__detail-label">Date of Birth</span>
                      <span className="member-dashboard__detail-value">
                        {memberDetails?.dob || "N/A"}
                      </span>
                    </Card>
                    <Card padding="md" className="member-dashboard__detail-card">
                      <span className="member-dashboard__detail-label">Address</span>
                      <span className="member-dashboard__detail-value">
                        {memberDetails?.address || "N/A"}
                      </span>
                    </Card>
                  </div>

                  <div className="member-dashboard__profile-stats">
                    <Card padding="lg" className="member-dashboard__stat-card">
                      <span className="member-dashboard__stat-label">Points</span>
                      <span className="member-dashboard__stat-value">
                        {memberDetails?.points || 0}
                      </span>
                    </Card>
                    <Card padding="lg" className="member-dashboard__stat-card">
                      <span className="member-dashboard__stat-label">Rank</span>
                      <span className="member-dashboard__stat-value">
                        {memberDetails?.rank || "N/A"}
                      </span>
                    </Card>
                  </div>
                </div>
              </Card>
            </section>

            {/* Active Books Section */}
            <section className="member-dashboard__section" id="activebooks@member">
              <h2 className="member-dashboard__section-title">Issued Books</h2>
              {activeBooks.length > 0 ? (
                <Card padding="md" className="member-dashboard__table-card">
                  <div className="member-dashboard__table-wrapper">
                    <table className="member-dashboard__table">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Book Cover</th>
                          <th>Book Name</th>
                          <th>From Date</th>
                          <th>To Date</th>
                          <th>Fine</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeBooks.map((data, index) => {
                          const fine = Math.floor(
                            (Date.parse(moment(new Date()).format("MM/DD/YYYY")) -
                              Date.parse(data.toDate)) /
                            86400000
                          ) <= 0
                            ? 0
                            : Math.floor(
                              (Date.parse(moment(new Date()).format("MM/DD/YYYY")) -
                                Date.parse(data.toDate)) /
                              86400000
                            ) * 10;

                          return (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td>
                                <img
                                  src={data.bookId?.imageUrl || noBookCover}
                                  alt={data.bookName}
                                  className="member-dashboard__book-cover"
                                  onError={(e) => { e.target.src = noBookCover }}
                                />
                              </td>
                              <td>{data.bookName}</td>
                              <td>{data.fromDate}</td>
                              <td>{data.toDate}</td>
                              <td>
                                <Badge variant={fine > 0 ? "danger" : "success"}>
                                  ₹{fine}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </Card>
              ) : (
                <Card padding="lg" className="member-dashboard__empty-state">
                  <BookIcon style={{ fontSize: 48, marginBottom: 16 }} />
                  <p>You don't have any issued books at the moment.</p>
                </Card>
              )}
            </section>

            {/* Reserved Books Section */}
            <section className="member-dashboard__section" id="reservedbooks@member">
              <h2 className="member-dashboard__section-title">Reserved Books</h2>
              {(() => {
                // Use reservations from memberDetails as source of truth, fallback to state
                // This ensures consistency with History section
                const allReservations = memberDetails?.reservations || reservations || [];

                // Filter only active reservations (pending, approved)
                // Make status check case-insensitive for robustness
                const activeReservations = allReservations.filter(r => {
                  if (!r) return false;
                  const status = (r.status || '').toLowerCase();
                  return status === 'pending' || status === 'approved';
                });

                return activeReservations.length > 0 ? (
                  <Card padding="md" className="member-dashboard__table-card">
                    <div className="member-dashboard__table-wrapper">
                      <table className="member-dashboard__table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Book Cover</th>
                            <th>Book Name</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeReservations.map((reservation, index) => (
                            <tr key={reservation._id || index}>
                              <td>{index + 1}</td>
                              <td>
                                <img
                                  src={reservation.bookId?.imageUrl || noBookCover}
                                  alt={reservation.bookId?.bookName}
                                  className="member-dashboard__book-cover"
                                  onError={(e) => { e.target.src = noBookCover }}
                                />
                              </td>
                              <td>{reservation.bookId?.bookName}</td>
                              <td>{moment(reservation.startDate).format("MM/DD/YYYY")}</td>
                              <td>{moment(reservation.endDate).format("MM/DD/YYYY")}</td>
                              <td>
                                <Badge variant={getStatusColor(reservation.status)}>
                                  {reservation.status}
                                </Badge>
                              </td>
                              <td>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  leadingIcon={<CancelIcon />}
                                  onClick={() => cancelReservation(reservation._id)}
                                >
                                  Cancel
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ) : (
                  <Card padding="lg" className="member-dashboard__empty-state">
                    <BookIcon style={{ fontSize: 48, marginBottom: 16 }} />
                    <p>You don't have any active reservations at the moment.</p>
                    <p style={{ fontSize: '0.9rem', marginTop: '8px', opacity: 0.7 }}>
                      All reservations appear in History, regardless of status.
                    </p>
                  </Card>
                );
              })()}
            </section>

            {/* History Section */}
            <section className="member-dashboard__section" id="history@member">
              <h2 className="member-dashboard__section-title">History</h2>
              {(() => {
                // Use reservations from memberDetails as source of truth, fallback to state
                // This ensures we have the most up-to-date data
                const allReservations = memberDetails?.reservations || reservations || [];

                // Combine transaction history and ALL reservation history
                const allHistory = [
                  // Add transaction history
                  ...(memberDetails?.prevTransactions?.map(transaction => ({
                    type: 'transaction',
                    id: transaction._id || transaction.id,
                    bookId: transaction.bookId,
                    bookName: transaction.bookName || 'Unknown Book',
                    fromDate: transaction.fromDate || 'N/A',
                    toDate: transaction.toDate || 'N/A',
                    returnDate: transaction.returnDate || 'N/A',
                    status: 'completed',
                    createdAt: transaction.createdAt || transaction.fromDate || new Date()
                  })) || []),
                  // Add ALL reservations to history
                  ...allReservations.map((reservation, idx) => ({
                    type: 'reservation',
                    id: reservation._id || reservation.id || `res-${idx}`,
                    bookId: reservation.bookId,
                    bookName: reservation.bookId?.bookName || reservation.bookName || 'Book Name Not Available',
                    fromDate: reservation.startDate ? moment(reservation.startDate).format("MM/DD/YYYY") : 'N/A',
                    toDate: reservation.endDate ? moment(reservation.endDate).format("MM/DD/YYYY") : 'N/A',
                    returnDate: reservation.endDate ? moment(reservation.endDate).format("MM/DD/YYYY") : "N/A",
                    status: reservation.status || 'pending',
                    createdAt: reservation.createdAt || reservation.startDate || new Date()
                  }))
                ].sort((a, b) => {
                  // Sort by creation date (most recent first)
                  // Use createdAt if available, otherwise use toDate
                  try {
                    const dateA = new Date(a.createdAt || a.toDate || a.returnDate);
                    const dateB = new Date(b.createdAt || b.toDate || b.returnDate);
                    return dateB - dateA;
                  } catch (e) {
                    return 0;
                  }
                });

                return allHistory.length > 0 ? (
                  <Card padding="md" className="member-dashboard__table-card">
                    <div className="member-dashboard__table-wrapper">
                      <table className="member-dashboard__table">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Type</th>
                            <th>Book Cover</th>
                            <th>Book Name</th>
                            <th>From</th>
                            <th>To</th>
                            <th>Status</th>
                            <th>End Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allHistory.map((item, index) => (
                            <tr key={item.id || index}>
                              <td>{index + 1}</td>
                              <td>
                                <Badge variant={item.type === 'transaction' ? 'primary' : 'secondary'}>
                                  {item.type === 'transaction' ? 'Transaction' : 'Reservation'}
                                </Badge>
                              </td>
                              <td>
                                <img
                                  src={item.bookId?.imageUrl || noBookCover}
                                  alt={item.bookName}
                                  className="member-dashboard__book-cover"
                                  onError={(e) => { e.target.src = noBookCover }}
                                />
                              </td>
                              <td>{item.bookName}</td>
                              <td>{item.fromDate}</td>
                              <td>{item.toDate}</td>
                              <td>
                                <Badge variant={getStatusColor(item.status)}>
                                  {item.status}
                                </Badge>
                              </td>
                              <td>{item.returnDate || item.toDate}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Card>
                ) : (
                  <Card padding="lg" className="member-dashboard__empty-state">
                    <HistoryIcon style={{ fontSize: 48, marginBottom: 16 }} />
                    <p>You don't have any history yet.</p>
                  </Card>
                );
              })()}
            </section>
          </div>
        </main>
      </div>

      <ProfileUpdateForm
        open={openProfileUpdate}
        handleClose={handleCloseProfileUpdate}
        userData={memberDetails}
        onUpdateSuccess={handleProfileUpdateSuccess}
      />
    </div>
  );
}

export default MemberDashboard;
